import { rpcEffectCaller } from "@crm/rpc-client";
import { ReviewCoursesRpc } from "@crm/rpc-contract/groups/review-courses";
import type {
  InsertReviewCourse,
  InsertReviewLesson,
  InsertReviewModule,
  InsertReviewResource,
} from "@crm/db/schema/fulfillment";
import { useMutationCommand } from "@crm/ui/hooks/use-mutation-command";
import type {
  ClearLessonProgressInput,
  SaveLessonProgressInput,
} from "../../domain/review-course-inputs";
import { Effect, Scope } from "effect";

// 类型化 RPC Effect 调用器（错误通道保留；Scope 由客户端 runtime 终端提供）
const reviewCourses = rpcEffectCaller(ReviewCoursesRpc);

// =============================================================================
// Types
// =============================================================================

type UpdateReviewCourseInput = {
  id: string;
  changes: Partial<InsertReviewCourse>;
};

type UpdateReviewModuleInput = {
  id: string;
  changes: Partial<InsertReviewModule>;
};

type UpdateReviewLessonInput = {
  id: string;
  changes: Partial<InsertReviewLesson>;
};

type UpdateReviewResourceInput = {
  id: string;
  changes: Partial<InsertReviewResource>;
};

// =============================================================================
// Mutation Shell（Effect-native）
// =============================================================================

/**
 * 复习课程 mutation 统一外壳。
 *
 * 课程/模块/课时/资源/进度五域互相级联，按 mutation 闭包各自携带
 * reactivityKeys；成功后统一声明式失效同屏订阅者，跨页由 staleTime 兜底。
 * 重复提交 drop（返回 Dropped），绝不悬挂。
 */
function useReviewMutation<V, A, E>(
  message: string,
  reactivityKeys: ReadonlyArray<unknown>,
  run: (vars: V) => Effect.Effect<A, E, Scope.Scope>,
) {
  const { state, isPending, execute, executePromise, reset } = useMutationCommand<A, E>({
    message,
    reactivityKeys,
  });
  return {
    execute: (vars: V) => execute(run(vars)),
    executePromise: (vars: V) => executePromise(run(vars)),
    isPending,
    state,
    reset,
  };
}

// =============================================================================
// Review Course Mutations
// =============================================================================

/**
 * Create a new review course
 *
 * Permission: review-course:write:all (admin only)
 */
export function useCreateReviewCourse() {
  return useReviewMutation(
    "创建复习课程失败",
    ["reviewCourse"],
    (data: InsertReviewCourse) => reviewCourses.createReviewCourse(data),
  );
}

/**
 * Update a review course
 *
 * Permission: review-course:write:all (admin only)
 */
export function useUpdateReviewCourse() {
  return useReviewMutation(
    "更新复习课程失败",
    ["reviewCourse"],
    (data: UpdateReviewCourseInput) => reviewCourses.updateReviewCourse(data),
  );
}

/**
 * Delete a review course
 *
 * Permission: review-course:write:all (admin only)
 */
export function useDeleteReviewCourse() {
  return useReviewMutation(
    "删除复习课程失败",
    ["reviewCourse", "reviewModule", "reviewLesson", "reviewResource"],
    (id: string) => reviewCourses.deleteReviewCourse({ id }),
  );
}

// =============================================================================
// Review Module Mutations
// =============================================================================

/**
 * Create a new review module
 *
 * Permission: review-course:write:all (admin only)
 */
export function useCreateReviewModule() {
  return useReviewMutation(
    "创建模块失败",
    ["reviewModule"],
    (data: InsertReviewModule) => reviewCourses.createReviewModule(data),
  );
}

/**
 * Update a review module
 *
 * Permission: review-course:write:all (admin only)
 */
export function useUpdateReviewModule() {
  return useReviewMutation(
    "更新模块失败",
    ["reviewModule"],
    (data: UpdateReviewModuleInput) => reviewCourses.updateReviewModule(data),
  );
}

/**
 * Delete a review module
 *
 * Permission: review-course:write:all (admin only)
 */
export function useDeleteReviewModule() {
  return useReviewMutation(
    "删除模块失败",
    ["reviewModule", "reviewLesson", "reviewResource"],
    (id: string) => reviewCourses.deleteReviewModule({ id }),
  );
}

// =============================================================================
// Review Lesson Mutations
// =============================================================================

/**
 * Create a new review lesson
 *
 * Permission: review-course:write:all (admin only)
 */
export function useCreateReviewLesson() {
  return useReviewMutation(
    "创建课时失败",
    ["reviewLesson"],
    (data: InsertReviewLesson) => reviewCourses.createReviewLesson(data),
  );
}

/**
 * Update a review lesson
 *
 * Permission: review-course:write:all (admin only)
 */
export function useUpdateReviewLesson() {
  return useReviewMutation(
    "更新课时失败",
    ["reviewLesson"],
    (data: UpdateReviewLessonInput) => reviewCourses.updateReviewLesson(data),
  );
}

/**
 * Delete a review lesson
 *
 * Permission: review-course:write:all (admin only)
 */
export function useDeleteReviewLesson() {
  return useReviewMutation(
    "删除课时失败",
    ["reviewLesson", "reviewResource"],
    (id: string) => reviewCourses.deleteReviewLesson({ id }),
  );
}

/**
 * Batch reorder review lessons
 *
 * Permission: review-course:write:all (admin only)
 */
export function useBatchReorderLessons() {
  return useReviewMutation(
    "调整课时顺序失败",
    ["reviewLesson"],
    (data: { items: Array<{ id: string; order: number }> }) =>
      reviewCourses.batchReorderLessons(data),
  );
}

// =============================================================================
// Review Resource Mutations
// =============================================================================

/**
 * Create a new review resource
 *
 * Permission: review-course:write:all (admin only)
 */
export function useCreateReviewResource() {
  return useReviewMutation(
    "创建资源失败",
    ["reviewResource"],
    (data: InsertReviewResource) => reviewCourses.createReviewResource(data),
  );
}

/**
 * Update a review resource
 *
 * Permission: review-course:write:all (admin only)
 */
export function useUpdateReviewResource() {
  return useReviewMutation(
    "更新资源失败",
    ["reviewResource"],
    (data: UpdateReviewResourceInput) => reviewCourses.updateReviewResource(data),
  );
}

/**
 * Delete a review resource
 *
 * Permission: review-course:write:all (admin only)
 */
export function useDeleteReviewResource() {
  return useReviewMutation(
    "删除资源失败",
    ["reviewResource"],
    (id: string) => reviewCourses.deleteReviewResource({ id }),
  );
}

// =============================================================================
// Lesson Progress Mutations
// =============================================================================

/**
 * Merge a canonical lesson progress observation
 *
 * 每个用户只能创建自己的进度记录
 */
export function useSaveLessonProgress() {
  return useReviewMutation(
    "保存学习进度失败",
    ["lessonProgress"],
    (data: SaveLessonProgressInput) => reviewCourses.saveLessonProgress(data),
  );
}

/**
 * Clear lesson progress by its stable lesson axis
 *
 * 每个用户只能删除自己的进度记录
 */
export function useClearLessonProgress() {
  return useReviewMutation(
    "删除学习进度失败",
    ["lessonProgress"],
    (data: ClearLessonProgressInput) => reviewCourses.clearLessonProgress(data),
  );
}

// =============================================================================
// Re-export types
// =============================================================================

export type {
  UpdateReviewCourseInput,
  UpdateReviewLessonInput,
  UpdateReviewModuleInput,
  UpdateReviewResourceInput,
};
